import classNames from 'classnames';
import { useSelector, useDispatch } from 'react-redux';
import SimpleAccordion from '../Accordion/SimpleAccordion';
import { togglePerformanceOverviewCollapse } from '../../../../../actions/gradebook';
import PerformanceGraphNullState from './PerformanceGraphNullState/PerformanceGraphNullState';
import PerformanceGraph from './PerformanceGraph';
import HowIsThisCalculatedModal from './HowIsThisCalculatedModal';
import PerformancePrimaryMetrics from './PerformancePrimaryMetrics';
import { useViewportWidth } from '../../hooks/useViewportWidth';
import { calculatePrimaryMetrics, isDataFromLastWeek } from './utils';
import s from './PerformanceOverview.module.css';

function PerformanceOverview({
  studentName,
  course,
  categoriesData,
  performanceData,
}) {
  const { isPhone, isMobile } = useViewportWidth();
  const isOpen = useSelector(
    (state) => !state.gradebook.collapsedPerformanceOverview,
  );

  const dispatch = useDispatch();

  if (!categoriesData || !Array.isArray(categoriesData)) {
    return null;
  }

  if (!course) {
    return null;
  }

  const safePerformanceData = performanceData || [];

  const handleToggle = () => {
    dispatch(togglePerformanceOverviewCollapse(!isOpen));
  };

  const allAssignments = categoriesData.flatMap(
    (category) => category.assignments || [],
  );

  const {
    assignmentsCompleted: currentAssignmentsCompleted,
    assignmentsPossible: currentAssignmentsPossible,
    missedAssignmentsSinceLastWeek,
    possibleAssignmentsSinceLastWeek,
  } = calculatePrimaryMetrics(allAssignments, safePerformanceData);

  const {
    grade: currentGrade,
    pointsEarned,
    pointsPossible,
    enrollmentStartDate,
  } = course;
  const previousPerformanceData =
    safePerformanceData[safePerformanceData.length - 1];

  // See if the most recent performance data is from within the last 7 days
  const hasDataFromPreviousWeek = isDataFromLastWeek(
    previousPerformanceData?.week_start_date,
  );

  let changeInGrade = null;
  if (hasDataFromPreviousWeek) {
    const previousGrade = previousPerformanceData.grade;
    changeInGrade = currentGrade - previousGrade;
  }

  return (
    <div
      className={classNames(s.performanceOverview, {
        [s.mobilePerformanceOverview]: isMobile,
      })}
    >
      <SimpleAccordion
        title="Student Performance Overview"
        isMobile={isMobile}
        isOpen={isOpen}
        onToggle={handleToggle}
        renderContent={({ isAccordionOpen }) => (
          <>
            {isOpen && (
              <div className={s.headerWithModal}>
                <HowIsThisCalculatedModal />
              </div>
            )}
            <PerformancePrimaryMetrics
              isAccordionOpen={isAccordionOpen}
              pointsEarned={pointsEarned}
              pointsPossible={pointsPossible}
              currentGrade={currentGrade}
              changeInGrade={changeInGrade}
              assignmentsCompleted={currentAssignmentsCompleted}
              assignmentsPossible={currentAssignmentsPossible}
              missedAssignmentsSinceLastWeek={missedAssignmentsSinceLastWeek}
              possibleAssignmentsSinceLastWeek={
                possibleAssignmentsSinceLastWeek
              }
            />
          </>
        )}
      >
        {!isPhone &&
          (safePerformanceData.length === 0 ? (
            <PerformanceGraphNullState />
          ) : (
            <PerformanceGraph
              studentName={studentName}
              performanceData={safePerformanceData}
              enrollmentStartDate={enrollmentStartDate}
            />
          ))}
      </SimpleAccordion>
    </div>
  );
}

export default PerformanceOverview;
